import { PlanningBlock } from "../pb-screen/models/planning-block/planning-block.interface";

/**
 * Merge a freshly reloaded full planning-block list against current store state, keeping
 * the store's copy of any PB whose timestamp is strictly newer than the reloaded one, and
 * re-injecting any guarded PB the server stopped returning.
 *
 * Membership normally follows `incoming` (planned-away rows drop, new rows appear); only
 * per-PB DATA is protected from being clobbered by an in-flight stale reload. On top of that,
 * `guardedSnapshots` are PBs the user just optimistically planned/unplanned — if the (flaky)
 * server omits one entirely, it is re-injected so it can never silently vanish. Mirrors the
 * board's "merge by identity, newest wins".
 */
export function mergeReloadedPlanningBlocks(
  existing: PlanningBlock[],
  incoming: PlanningBlock[],
  guardedSnapshots: PlanningBlock[] = [],
): PlanningBlock[] {
  const incomingList = incoming || [];
  const existingList = existing || [];
  const byId = new Map(existingList.map(pb => [pb.id, pb]));

  let merged: PlanningBlock[];
  if (!incomingList.length || !existingList.length) {
    merged = [...incomingList];
  } else {
    merged = incomingList.map(pb => {
      const prev = byId.get(pb.id);
      if (prev?.timestamp && pb.timestamp &&
          new Date(prev.timestamp.toString()).getTime() > new Date(pb.timestamp.toString()).getTime()) {
        return prev; // store copy is newer — keep it
      }
      return pb;
    });
  }

  // Defensive guard: a PB the user just acted on must never disappear because the server
  // stopped returning it. Re-inject the live store copy (preferred) or the action-time snapshot.
  if (guardedSnapshots.length) {
    const present = new Set(merged.map(pb => String(pb.id)));
    for (const snap of guardedSnapshots) {
      const key = String(snap.id);
      if (!present.has(key)) {
        merged.push(byId.get(snap.id) || snap);
        present.add(key);
      }
    }
  }

  return merged;
}
